import type { UseMutationResult } from "@tanstack/react-query";
import { useInfiniteQuery, useMutation, useQuery } from "@tanstack/react-query";
import { CarriersResponseSchema } from "src/schema/carrier-schema";
import type {
	TCarrierFuelPrice,
	TCarrierFuelPriceResponse,
	TCarrierInfoBody,
	TCarriersResponse,
	TRequestCarriersResponse,
	TUserCarriersResponse,
} from "src/type/carrier";
import axiosInstance from "src/utils/axios";

//Carrier

const getCarriers = async ({
	page,
	pageSize,
	search,
}: {
	page: string;
	pageSize: number;
	search: string;
}): Promise<TCarriersResponse> => {
	// const baseUrl = nextUrl ? `${nextUrl.substring(nextUrl.lastIndexOf("/"), nextUrl.length)}` : "/user-search-trip?";
	const baseUrl =
		page !== "" ? page : `/api/app/core/v1.0/carrier/?page-size=${pageSize}&search=${search}`;
	const { data } = await axiosInstance.get(baseUrl);
	const result = CarriersResponseSchema.safeParse(data);
	if (!result.success) {
		throw new Error(`Parse carriers Response Error${JSON.stringify(result.error)}`);
	}
	return data;
};

const updateCarrierInfo = async (body: TCarrierInfoBody): Promise<{ message: string }> => {
	const { data } = await axiosInstance.post(`/api/app/core/v1.0/carrier/request`, body);
	return data;
};
const updateCarrierFuelPrice = async (body: TCarrierFuelPrice): Promise<{ message: string }> => {
	const { data } = await axiosInstance.put(`/api/app/core/v1.0/carrier/fuel-price/`, body);
	return data;
};

const getCarrierFuelPrice = async (): Promise<TCarrierFuelPriceResponse> => {
	const { data } = await axiosInstance.get(`/api/app/core/v1.0/carrier/fuel-price/`);
	return data;
};

const requestCarrierList = async (): Promise<TRequestCarriersResponse> => {
	const { data } = await axiosInstance.get(`/api/app/core/v1.0/request-carrier/`);
	return data;
};
const deleteRequestCarrier = async (carrierId: number): Promise<{ message: string }> => {
	const { data } = await axiosInstance.delete(`/api/app/core/v1.0/request-carrier/${carrierId}/`);
	return data;
};
const deleteUserCarrier = async (carrierId: number): Promise<{ message: string }> => {
	const { data } = await axiosInstance.delete(`/api/app/core/v1.0/user-carrier/${carrierId}/`);
	return data;
};
const userCarrierList = async (): Promise<TUserCarriersResponse> => {
	const { data } = await axiosInstance.get(`/api/app/core/v1.0/user-carrier/`);
	return data;
};

const usePostCarrierInfo = (): UseMutationResult<{ message: string }, unknown, TCarrierInfoBody> =>
	useMutation({
		mutationFn: (body: TCarrierInfoBody) => updateCarrierInfo(body),
	});

const useUpdateCarrierFuelPrice = (): UseMutationResult<
	{ message: string },
	unknown,
	TCarrierFuelPrice
> =>
	useMutation({
		mutationFn: (body: TCarrierFuelPrice) => updateCarrierFuelPrice(body),
	});

function useGetCarriers({
	pageSize,
	enable = true,
	search,
}: {
	search: string;
	pageSize: number;
	enable?: boolean;
}) {
	return useInfiniteQuery({
		queryKey: ["carriers", search],
		queryFn: ({ pageParam }: { pageParam: string }) =>
			getCarriers({ search, page: pageParam, pageSize }),
		getNextPageParam: (lastPage) => lastPage.next,
		initialPageParam: "",
		enabled: enable,
	});
}
function useGetRequestCarriers({ enable = true }: { enable?: boolean }) {
	return useQuery({
		queryKey: ["request-carriers"],
		queryFn: () => requestCarrierList(),
		enabled: enable,
	});
}

function useGetUserCarriers({ enable = true }: { enable?: boolean }) {
	return useQuery({
		queryKey: ["user-carriers"],
		queryFn: () => userCarrierList(),
		enabled: enable,
	});
}
function useGetCarrierFuelPrice({ enable = true }: { enable?: boolean }) {
	return useQuery({
		queryKey: ["carrier-fuel"],
		queryFn: () => getCarrierFuelPrice(),
		enabled: enable,
	});
}
const useDeleteRequestCarrier = (): UseMutationResult<
	{ message: string },
	unknown,
	{ carrierId: number }
> =>
	useMutation({
		mutationFn: ({ carrierId }: { carrierId: number }) => deleteRequestCarrier(carrierId),
	});

const useDeleteUserCarrier = (): UseMutationResult<
	{ message: string },
	unknown,
	{ carrierId: number }
> =>
	useMutation({
		mutationFn: ({ carrierId }: { carrierId: number }) => deleteUserCarrier(carrierId),
	});

export {
	useDeleteRequestCarrier,
	useDeleteUserCarrier,
	useGetCarrierFuelPrice,
	useGetCarriers,
	useGetRequestCarriers,
	useGetUserCarriers,
	usePostCarrierInfo,
	useUpdateCarrierFuelPrice,
};
